import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SESSION_MODE_DETAILS, } from "@evyely/types";
import { useSession } from "../hooks/useSession";
import { useAudio } from "../hooks/useAudio";
import { OverlayPanel } from "../components/overlay/OverlayPanel";
import { trpc } from "../lib/trpc";
import { Sparkles } from "lucide-react";
export default function SessionPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const session = useSession();
    const audio = useAudio({ onChunk: session.sendAudioChunk });
    const [question, setQuestion] = useState("");
    const sessionMode = (searchParams.get("mode") === "interview"
        ? "interview"
        : "meeting");
    const modeDetails = SESSION_MODE_DETAILS[sessionMode];
    const feedbackMutation = trpc.session.list.useQuery({ limit: 1 });
    const start = async () => {
        await session.connect(sessionMode);
        await audio.start();
        session.sendAudioConfig(16000, 1);
    };
    const stop = () => {
        audio.stop();
        session.disconnect();
        void feedbackMutation.refetch();
    };
    const askForAnswer = () => {
        const sent = session.askQuestion(question.trim(), "assist");
        if (sent)
            setQuestion("");
    };
    useEffect(() => {
        return () => {
            audio.stop();
            session.disconnect();
        };
    }, []);
    const isActive = audio.isCapturing;
    return (_jsxs("div", { style: {
            display: "flex",
            height: "100vh",
            background: "linear-gradient(135deg, #0d0d14 0%, #0f0a1a 50%, #080d16 100%)",
            overflow: "hidden",
            position: "relative",
        }, children: [_jsx("div", { style: {
                    position: "absolute", top: "20%", left: "30%",
                    width: 500, height: 500,
                    background: "radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)",
                    pointerEvents: "none",
                } }), _jsxs("div", { style: { flex: 1, display: "flex", flexDirection: "column", padding: "20px 20px 20px 24px", overflow: "hidden" }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10 }, children: [_jsx("div", { style: {
                                            width: 28, height: 28, borderRadius: 9,
                                            background: "linear-gradient(135deg, rgba(139,92,246,0.9), rgba(99,102,241,0.9))",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 12, color: "#fff", flexShrink: 0,
                                        }, children: _jsx(Sparkles, { size: 14, strokeWidth: 2 }) }), _jsxs("div", { children: [_jsx("h1", { style: { fontSize: 15, fontWeight: 700, color: "rgba(255,255,255,0.85)", margin: 0 }, children: "Live Session" }), _jsxs("p", { style: { fontSize: 11, color: "rgba(165,168,255,0.75)", margin: "2px 0 0" }, children: [modeDetails.label, " Mode"] }), session.sessionId && (_jsxs("p", { style: { fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0 }, children: [session.sessionId.slice(0, 8), "\u2026"] }))] })] }), _jsxs("div", { style: { display: "flex", gap: 8 }, children: [_jsx("button", { onClick: () => navigate("/dashboard"), style: {
                                            background: "rgba(255,255,255,0.05)",
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            color: "rgba(255,255,255,0.4)",
                                            padding: "6px 12px",
                                            borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 500,
                                        }, children: "\u2190 Dashboard" }), _jsx("button", { onClick: isActive ? stop : start, style: {
                                            background: isActive
                                                ? "rgba(239,68,68,0.18)"
                                                : "rgba(99,102,241,0.22)",
                                            border: `1px solid ${isActive ? "rgba(239,68,68,0.3)" : "rgba(99,102,241,0.35)"}`,
                                            color: isActive ? "rgba(248,113,113,0.9)" : "rgba(165,168,255,0.9)",
                                            padding: "6px 16px",
                                            borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600,
                                            backdropFilter: "blur(8px)",
                                            transition: "all 0.2s",
                                        }, children: isActive ? "Stop" : "Start session" })] })] }), (session.error ?? audio.error) && (_jsx("div", { style: {
                            background: "rgba(239,68,68,0.1)",
                            border: "1px solid rgba(239,68,68,0.2)",
                            color: "rgba(248,113,113,0.9)",
                            padding: "10px 14px",
                            borderRadius: 10, fontSize: 12, marginBottom: 14,
                        }, children: session.error ?? audio.error })), !isActive && session.transcripts.length === 0 && (_jsxs("div", { style: {
                            flex: 1, display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center", gap: 14,
                            background: "rgba(255,255,255,0.02)",
                            borderRadius: 18,
                            border: "1px dashed rgba(255,255,255,0.07)",
                        }, children: [_jsx("div", { style: { fontSize: 40, opacity: 0.25 }, children: "\uD83C\uDF99\uFE0F" }), _jsxs("p", { style: { color: "rgba(255,255,255,0.3)", fontSize: 13, fontWeight: 500, margin: 0 }, children: [modeDetails.startLabel, " to begin transcribing"] }), _jsx("p", { style: { color: "rgba(255,255,255,0.15)", fontSize: 11, margin: 0 }, children: modeDetails.description })] })), session.transcripts.length > 0 && (_jsx("div", { style: {
                            flex: 1, overflowY: "auto",
                            display: "flex", flexDirection: "column", gap: 10,
                            background: "rgba(255,255,255,0.025)",
                            borderRadius: 16, padding: 18,
                            border: "1px solid rgba(255,255,255,0.05)",
                        }, children: session.transcripts.slice(-30).map((t) => (_jsxs("div", { style: {
                                opacity: t.isFinal ? 1 : 0.45,
                                display: "flex", gap: 10,
                                transition: "opacity 0.2s",
                            }, children: [_jsx("span", { style: {
                                        fontSize: 10, fontWeight: 700,
                                        color: t.speaker === "user" ? "rgba(139,92,246,0.7)" : "rgba(255,255,255,0.25)",
                                        textTransform: "uppercase", letterSpacing: "0.06em",
                                        minWidth: 32, paddingTop: 3, flexShrink: 0,
                                    }, children: t.speaker === "user" ? "You" : "Them" }), _jsxs("p", { style: { fontSize: 13, color: "rgba(255,255,255,0.72)", lineHeight: 1.55, margin: 0 }, children: [t.text, !t.isFinal && _jsx("span", { style: { opacity: 0.35 }, children: " \u258C" })] })] }, t.id))) })), isActive && (_jsxs("div", { style: {
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "8px 14px", marginTop: 14,
                            background: "rgba(239,68,68,0.08)",
                            border: "1px solid rgba(239,68,68,0.15)",
                            borderRadius: 9, width: "fit-content",
                        }, children: [_jsx("div", { style: {
                                    width: 6, height: 6, borderRadius: "50%",
                                    background: "#ef4444", boxShadow: "0 0 6px #ef4444",
                                    animation: "recPulse 1.2s ease-in-out infinite",
                                } }), _jsx("span", { style: { fontSize: 11, color: "rgba(248,113,113,0.8)", fontWeight: 600 }, children: "Recording" }), _jsx("style", { children: `@keyframes recPulse { 0%,100%{opacity:1} 50%{opacity:0.3} }` })] })), isActive && (_jsxs("div", { style: {
                            display: "flex", gap: 8, marginTop: 14,
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.07)",
                            borderRadius: 12, padding: 10,
                        }, children: [_jsx("input", { value: question, onChange: (e) => setQuestion(e.target.value), onKeyDown: (e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        askForAnswer();
                                    }
                                }, placeholder: "Ask for an answer, or leave blank to answer from transcript", style: {
                                    flex: 1,
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 8,
                                    color: "rgba(255,255,255,0.82)",
                                    fontSize: 12,
                                    outline: "none",
                                    padding: "8px 10px",
                                } }), _jsx("button", { onClick: askForAnswer, disabled: session.isStreaming, style: {
                                    background: session.isStreaming ? "rgba(255,255,255,0.06)" : "rgba(99,102,241,0.22)",
                                    border: `1px solid ${session.isStreaming ? "rgba(255,255,255,0.08)" : "rgba(99,102,241,0.35)"}`,
                                    color: session.isStreaming ? "rgba(255,255,255,0.35)" : "rgba(165,168,255,0.9)",
                                    padding: "8px 14px",
                                    borderRadius: 8,
                                    fontSize: 12,
                                    cursor: session.isStreaming ? "default" : "pointer",
                                    fontWeight: 600,
                                }, children: "Answer" })] }))] }), _jsx("div", { style: {
                    width: 360,
                    padding: "20px 20px 20px 0",
                    display: "flex",
                    flexDirection: "column",
                }, children: _jsx("div", { style: { flex: 1 }, children: _jsx(OverlayPanel, { transcripts: session.transcripts, currentSuggestion: session.currentSuggestion, isStreaming: session.isStreaming, isConnected: session.isConnected }) }) })] }));
}
